// STIMULUS CONTROLLER: filter_assessment_modal_controller.js
import { Controller } from "@hotwired/stimulus"
import analytics from "../src/application/analytics"

export default class extends Controller {
  static targets = [
    "modal", "filterForm", "dateField", "assessmentList", "searchField","tableContent",
    "hiddenPurposes","hiddenDisciplines", "selectedFiltersContainer","assessmentHeader",
    "checkbox", "selectedIdsField", "assessmentMeta", "availableCount"
  ]

  static values = {
    sortColumn: String,
    sortDirection: String
  }

  connect() {
    this.initializeDatePickers()
    this.page = 1
    this.loading = false
    this.endOfResults = false
    this.selectedIds = new Set()
    this.tableContentTarget.addEventListener("scroll", () => this.onScroll())
    this.searchFieldTarget.addEventListener("input", this.debounce(() => this.onSearch(), 400))
    this.updateSelectedFiltersDisplay()
    this.updateSelectedIds()
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.handleEscape)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape);
  }

  open(event) {
    event.preventDefault()
    this.page = 1
    this.endOfResults = false
    this.assessmentListTarget.innerHTML = ""
    this.initializeDatePickers()
    this.fetchFilteredAssessments()
  }

  close(event) {
    if (event) event.preventDefault();
    this.clearFilters();
    window.location.reload
  }

  clearFilters(event) {
    if (event) event.preventDefault();
    this.filterFormTarget.reset()
    this.page = 1
    this.endOfResults = false
    this.clearSelectedFilters()
    this.assessmentListTarget.innerHTML = ""
    this.selectedIdsFieldTarget.value = []
    this.selectedIds = new Set()
    this.fetchFilteredAssessments()
  }

  applyFilter(event) {
    this.page = 1
    this.endOfResults = false
    this.assessmentListTarget.innerHTML = ""
    this.selectedIdsFieldTarget.value = []
    this.storeSelectedFilters()
    this.selectedIds = new Set()
    this.fetchFilteredAssessments()
  }

  onScroll() {
    if (this.loading || this.endOfResults) return
    const container = this.tableContentTarget
    const nearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50
    if (nearBottom) {
      this.page++
      this.fetchFilteredAssessments()
    }
  }

  onSearch() {
    this.page = 1
    this.endOfResults = false
    this.assessmentListTarget.innerHTML = ""
    this.fetchFilteredAssessments()
  }

  fetchFilteredAssessments() {
    this.loading = true;

    const formData = this.buildFormData();
    const queryString = new URLSearchParams(formData).toString();

    fetch(`/report_dashboard/filter_assessments?${queryString}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => this.handleAssessmentResponse(html))
      .catch(() => { this.loading = false });
  }

  buildFormData() {
    const formData = new FormData(this.filterFormTarget);
    formData.append("page", this.page);

    if (this.hasSortColumnValue && this.sortColumnValue) {
      formData.append("sort_column", this.sortColumnValue);
    }

    if (this.hasSortDirectionValue && this.sortDirectionValue) {
      formData.append("sort_direction", this.sortDirectionValue);
    }

    return formData;
  }

  handleAssessmentResponse(html) {
    const trimmed = html.trim();
    const hasAssessments = trimmed.includes("data-assessment-id");

    if (trimmed === "") {
      this.endOfResults = true;
    } else if (hasAssessments) {
      this.insertAssessmentsFromHtml(trimmed);
    } else {
      this.assessmentListTarget.insertAdjacentHTML("beforeend", html);
      this.assessmentHeaderTarget.classList.add("d-none");
      this.availableCountTarget.textContent = `0 Available`;
    }

    this.loading = false;
  }

  insertAssessmentsFromHtml(html) {
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = html;

    const assessmentsHtml = tempContainer.querySelector("#assessments-table")?.innerHTML || html;
    this.assessmentListTarget.insertAdjacentHTML("beforeend", assessmentsHtml);

    const meta = tempContainer.querySelector("#filtered-assessment-data");
    if (meta) {
      this.allAssessmentIds = meta.dataset.assessmentIds.split(',').map(Number);
      this.totalCount = this.allAssessmentIds.length;

      if (this.hasAvailableCountTarget) {
        this.availableCountTarget.textContent = `${this.totalCount} Available`;
      }
    }

    this.assessmentHeaderTarget.classList.remove("d-none");

    this.checkboxTargets.forEach(cb => {
      const id = parseInt(cb.closest('tr').dataset.assessmentId);
      cb.checked = this.selectedIds.has(id);
    });
  }

  initializeDatePickers() {
    const today = new Date();
    const startDefault = new Date("2024-07-01");

    this.dateFieldTargets.forEach((dateField) => {
      const isStart = dateField.name === "start_date";
      const isEnd = dateField.name === "end_date";

      const defaultDate = isStart ? startDefault : isEnd ? today : null;

      $(dateField)
        .datepicker({
          numberOfMonths: 1,
          dateFormat: "M d, yy",
          defaultDate: defaultDate,
          onSelect: () => this.applyFilter()
        })
        .datepicker("setDate", defaultDate)
        .on("keypress", e => e.preventDefault());

      dateField.readOnly = true;
    });
  }

  storeSelectedFilters() {
    const selectedDisciplines = Array.from(this.filterFormTarget.querySelectorAll("input[name='discipline[]']:checked"))
      .map(input => input.value)
    const selectedPurposes = Array.from(this.filterFormTarget.querySelectorAll("input[name='purpose[]']:checked"))
      .map(input => input.value)

    this.filterFormTarget.querySelector("input[name='selected_disciplines']").value = selectedDisciplines.join(',')
    this.filterFormTarget.querySelector("input[name='selected_purposes']").value = selectedPurposes.join(',')

    this.updateSelectedFiltersDisplay()
  }

  clearSelectedFilters() {
    this.filterFormTarget.querySelector("input[name='selected_disciplines']").value = ""
    this.filterFormTarget.querySelector("input[name='selected_purposes']").value = ""
    this.updateSelectedFiltersDisplay()
  }

  updateSelectedFiltersDisplay() {
    const container = this.selectedFiltersContainerTarget
    container.innerHTML = ""

    const disciplines = this.filterFormTarget.querySelector("input[name='selected_disciplines']").value.split(',').filter(Boolean)
    const purposes = this.filterFormTarget.querySelector("input[name='selected_purposes']").value.split(',').filter(Boolean)
    const titleize = str => str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())

    const filters = [
      ...disciplines.map(v => ({ type: 'discipline', value: v })),
      ...purposes.map(v => ({ type: 'purpose', value: v }))
    ]

    filters.forEach(filter => {
      const tag = document.createElement("span")
      tag.className = "filter-easy-clear-button me-2"
      tag.innerHTML = `${titleize(filter.value)} <a href="#" class="text-black ms-1" data-action="click->filter-assessment-modal#removeFilter" data-filter-type="${filter.type}" data-filter-value="${filter.value}">&times;</a>`
      container.appendChild(tag)
    })
  }

  removeFilter(event) {
    event.preventDefault()
    const type = event.target.dataset.filterType
    const value = event.target.dataset.filterValue
    const inputSelector = `input[name='${type}[]'][value='${value}']`
    const input = this.filterFormTarget.querySelector(inputSelector)
    if (input) input.checked = false
    this.applyFilter(event)
  }

  debounce(func, wait) {
    let timeout
    return function(...args) {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  selectAllShown() {
    if (!this.allAssessmentIds) return

    // Update hidden input via Stimulus target
    if (this.hasSelectedIdsFieldTarget) {
      this.selectedIds = new Set(this.allAssessmentIds)
      this.selectedIdsFieldTarget.value = this.allAssessmentIds.join(',')
    }

    // Check all visible checkboxes
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
    })
  }

  sort(event) {
    const clickedColumn = event.target.dataset.sortColumn;
    const currentDirection = this.sortDirectionValue;
    const currentColumn = this.sortColumnValue;

    if (currentColumn === clickedColumn) {
      this.sortDirectionValue = currentDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortDirectionValue = event.target.dataset.sortOrder || "asc";
    }

    this.sortColumnValue = clickedColumn;
    this.applyFilter();
  }

  updateSelectedIds() {
    this.checkboxTargets.forEach(cb => {
      const id = parseInt(cb.closest('tr').dataset.assessmentId)
      if (cb.checked) this.selectedIds.add(id)
      else this.selectedIds.delete(id)
    })

    this.selectedIdsFieldTarget.value = [...this.selectedIds].join(',')
  }

  redirectToSelectedResults(event) {
    const ids = this.selectedIdsFieldTarget.value.split(',').filter(Boolean)
    if (ids.length === 0) {
      alert("Please select at least one assessment.")
      return
    }

    const filtersUsed = this.extractFilterData()
    this.trackSelectionEvents(ids, filtersUsed)
    this.navigateToReports(ids)
  }

  extractFilterData() {
    return {
      disciplines: this.filterFormTarget.querySelector("input[name='selected_disciplines']").value.split(',').filter(Boolean),
      purposes: this.filterFormTarget.querySelector("input[name='selected_purposes']").value.split(',').filter(Boolean),
      search_term: this.searchFieldTarget?.value?.trim() || null,
      start_date: this.filterFormTarget.querySelector("input[name='start_date']")?.value?.trim() || null,
      end_date: this.filterFormTarget.querySelector("input[name='end_date']")?.value?.trim() || null
    }
  }

  trackSelectionEvents(ids, filtersUsed) {
    analytics.track("calibrate_selection_filters", {
      page: window.location.href,
      source: 'Calibrate selection modal',
      filters_used: filtersUsed
    })

    analytics.track("calibrate_report_usage", {
      page: window.location.href,
      source: 'Calibrate selection modal',
      assessments_selected: ids
    })
  }

  navigateToReports(ids) {
    const url = ids.length === 1
      ? `/assessment/reports/educator/cohort_progresses/${ids[0]}`
      : `/assessment/reports/educator/cohort_progresses?${ids.map(id => `ids[]=${id}`).join('&')}`

    window.location.href = url
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }
}
